import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { policyAccessQuery, savePolicyCategoryRoles, type PolicyAccessCategory } from '../api/easeedeskPolicies'
import { Modal } from '../ui/Modal'
import { toast } from '../ui/notify'
import { SelectPicker } from '../ui/SelectPicker'
import { Spinner } from '../ui/Spinner'

/**
 * Setarile de acces ale IT Policies: rolurile care vad fiecare categorie.
 *
 * Aceeasi regula ca la categoriile bibliotecii de solutii ("Who can see it"):
 * nimic bifat = toata lumea, ca inainte; cu roluri bifate, politicile
 * categoriei le vad numai acele roluri (si adminul). Serverul o tine pe
 * fiecare usa - lista, cautarea, continutul, NIS2, fila "Standards" a
 * tichetului; aici doar se alege.
 *
 * Butonul apare numai pentru cine are `easeedesk.it_policies.edit`.
 */
export function PolicyCategoryAccessButton() {
  const [open, setOpen] = useState(false)

  return (
    <>
      <button type="button" className="btn btn-default btn-sm policy-category-access" onClick={() => setOpen(true)}>
        <i className="fas fa-user-lock" aria-hidden="true"></i> Category access
      </button>
      {open && <PolicyCategoryAccessModal onClose={() => setOpen(false)} />}
    </>
  )
}

export function PolicyCategoryAccessModal({ onClose }: { onClose(): void }) {
  const q = useQuery(policyAccessQuery)

  return (
    <Modal id="policyCategoryAccessModal" open onClose={onClose} title="Category access">
      {q.isError && <div className="alert alert-danger">Something went wrong: {q.error.message}</div>}
      {!q.data && !q.isError && <Spinner />}
      {q.data && !q.data.ready && <div className="alert alert-info">Category access is not available yet - the database update has not run.</div>}
      {q.data && q.data.ready && (
        <>
          <p className="adm-muted">Leave a category on Everybody to keep it open to everyone who can open IT Policies.</p>
          <table className="table table-hover" id="policy-category-access">
            <thead>
              <tr>
                <th>Category</th>
                <th>Who can see it</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {q.data.categories.length === 0 && (
                <tr>
                  <td colSpan={3}>No categories found.</td>
                </tr>
              )}
              {q.data.categories.map((c) => (
                <CategoryRow key={c.ID} category={c} roles={q.data.roles} />
              ))}
            </tbody>
          </table>
        </>
      )}
      <div className="aright">
        <button type="button" className="btn btn-default" onClick={onClose}>
          Close
        </button>
      </div>
    </Modal>
  )
}

function CategoryRow({ category, roles }: { category: PolicyAccessCategory; roles: { id: number; name: string }[] }) {
  const client = useQueryClient()
  const [picked, setPicked] = useState<string[]>(category.roles.map(String))
  const [busy, setBusy] = useState(false)
  const changed = picked.slice().sort().join(',') !== category.roles.map(String).sort().join(',')

  const save = () => {
    setBusy(true)
    savePolicyCategoryRoles(category.ID, picked.map(Number))
      .then((out) => {
        if (!out.ok) {
          toast(out.message ?? 'Not saved.', 'Error', 'error')
          return
        }
        toast('Category access saved.', 'Success', 'success')
        void client.invalidateQueries({ queryKey: ['easeedesk', 'it-policies'] })
      })
      .catch((err: unknown) => toast(err instanceof Error ? err.message : 'Not saved.', 'Error', 'error'))
      .finally(() => setBusy(false))
  }

  return (
    <tr>
      <td>{category.category_name}</td>
      <td style={{ minWidth: 220 }}>
        <SelectPicker
          id={`policy-category-roles-${category.ID}`}
          className="form-control"
          multiple
          title="Everybody"
          options={roles.map((r) => ({ value: String(r.id), label: r.name }))}
          value={picked}
          onChange={setPicked}
        />
      </td>
      <td className="aright">
        <button type="button" className="btn btn-sm btn-primary save-category-access" disabled={!changed || busy} onClick={save}>
          Save
        </button>
      </td>
    </tr>
  )
}
